import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import './ReframingActivity.scss';
import { VideoGateContext } from '../../VideoGateContext';
import { useBadge } from '../../../../../../customHooks/useBadge';
import ActivityBadge from '../../../../../shared/badges/ActivityBadge';
import ActivityCompletionNotice from '../../../../../activities/ActivityCompletionNotice';

const rows = [
  {
    id: 'row-1',
    stressBehavior: 'Whining',
    upsettingThought:
      'I am so tired from work and this is the last thing I want to hear',
    commonParentResponse: 'Annoyance or yelling',
    reframedThought: 'He’s probably tired too and seems really frustrated.',
    newParentResponse:
      'Descriptive Commenting and Distraction: “It sounds like you’re tired. I’m tired too. Let’s go read a story!”',
  },
  {
    id: 'row-2',
    stressBehavior: 'Constant questions',
    upsettingThought: 'If you ask one more question I’m going to scream.',
    commonParentResponse: 'Threatening or criticism',
    reframedThought: 'He’s so curious; I guess he really likes to learn.',
    newParentResponse:
      'Child centered time: “You’re very curious today. Let’s go find a book to read that may help answer some of your questions”',
  },
  {
    id: 'row-3',
    stressBehavior: 'Sibling rivalry in the car',
    upsettingThought:
      'Driving is already stressful, If those two don’t stop fighting, I’m going to send them out of the car and they can walk home.',
    commonParentResponse: 'Yelling, criticizing, threatening with punishment',
    reframedThought:
      'They are fighting because they’re bored. Let’s think of a game to play.',
    newParentResponse:
      'Distraction: “Let’s play a game. Name people we know whose name begins with the letter S”',
  },
  {
    id: 'row-4',
    stressBehavior: 'Refusing to eat at mealtime',
    upsettingThought:
      'I spent all this time cooking and now they won’t eat it!',
    commonParentResponse: 'Threating with punishment',
    reframedThought: 'She just needs a little more time to eat',
    newParentResponse:
      'When-then: “When you finish your dinner, then we can read a book together”',
  },
];

const thoughtCards = {
  'thought-1': {
    id: 'thought-1',
    rowId: 'row-1',
    content: 'He’s probably tired too and seems really frustrated.',
  },
  'thought-2': {
    id: 'thought-2',
    rowId: 'row-2',
    content: 'He’s so curious; I guess he really likes to learn.',
  },
  'thought-3': {
    id: 'thought-3',
    rowId: 'row-3',
    content:
      'They are fighting because they’re bored. Let’s think of a game to play.',
  },
  'thought-4': {
    id: 'thought-4',
    rowId: 'row-4',
    content: 'She just needs a little more time to eat.',
  },
};

const initialColumns = {
  bank: ['thought-3', 'thought-1', 'thought-4', 'thought-2'],
  'row-1': [],
  'row-2': [],
  'row-3': [],
  'row-4': [],
};

const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      setEnabled(true);
    });

    return () => {
      cancelAnimationFrame(animationFrame);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const ReframingActivity = ({
  badge = null,
  showCompletionNotice = false,
  showIntro = true,
}) => {
  const { registerActivityRequirement, markActivityCompleted } =
    React.useContext(VideoGateContext);
  const [columns, setColumns] = useState(initialColumns);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [draggingCardSize, setDraggingCardSize] = useState(null);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [{ showBadge }, dispatch] = useBadge(false);
  const cardRefs = useRef({});
  const completionHandledRef = useRef(false);
  const isComplete = rows.every((row) => columns[row.id].length > 0);

  const flashError = () => {
    setShowErrorMessage(true);
    window.clearTimeout(ReframingActivity.errorTimer);
    ReframingActivity.errorTimer = window.setTimeout(() => {
      setShowErrorMessage(false);
    }, 2000);
  };

  useEffect(() => {
    registerActivityRequirement();
  }, [registerActivityRequirement]);

  useEffect(() => {
    if (isComplete && !completionHandledRef.current) {
      completionHandledRef.current = true;
      markActivityCompleted();

      if (badge) {
        dispatch({ type: 'show', payload: badge });
      }
    }
  }, [badge, dispatch, isComplete, markActivityCompleted]);

  useEffect(() => {
    return () => {
      window.clearTimeout(ReframingActivity.errorTimer);
    };
  }, []);

  const handleDragStart = (start) => {
    const draggedNode = cardRefs.current[start.draggableId];

    setDraggingCardId(start.draggableId);
    setDraggingCardSize(
      draggedNode
        ? {
            width: draggedNode.offsetWidth,
            height: draggedNode.offsetHeight,
          }
        : null,
    );
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      setDraggingCardId(null);
      setDraggingCardSize(null);
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      setDraggingCardId(null);
      setDraggingCardSize(null);
      return;
    }

    const sourceItems = Array.from(columns[source.droppableId]);
    const destinationItems = Array.from(columns[destination.droppableId]);
    const movedCard = thoughtCards[draggableId];

    if (
      destination.droppableId !== 'bank' &&
      movedCard.rowId !== destination.droppableId
    ) {
      flashError();
      setDraggingCardId(null);
      setDraggingCardSize(null);
      return;
    }

    if (
      destination.droppableId !== 'bank' &&
      destinationItems.length > 0 &&
      source.droppableId !== destination.droppableId
    ) {
      flashError();
      setDraggingCardId(null);
      setDraggingCardSize(null);
      return;
    }

    sourceItems.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      sourceItems.splice(destination.index, 0, draggableId);
      setColumns({
        ...columns,
        [source.droppableId]: sourceItems,
      });
      setDraggingCardId(null);
      setDraggingCardSize(null);
      return;
    }

    destinationItems.splice(destination.index, 0, draggableId);

    setColumns({
      ...columns,
      [source.droppableId]: sourceItems,
      [destination.droppableId]: destinationItems,
    });
    setDraggingCardId(null);
    setDraggingCardSize(null);
  };

  const handleThoughtSelection = (rowId, thoughtId) => {
    const selectedThought = thoughtCards[thoughtId];

    if (!selectedThought || selectedThought.rowId !== rowId) {
      flashError();
      return;
    }

    setColumns((currentColumns) => {
      if (
        currentColumns[rowId].length > 0 ||
        !currentColumns.bank.includes(thoughtId)
      ) {
        return currentColumns;
      }

      return {
        ...currentColumns,
        bank: currentColumns.bank.filter((id) => id !== thoughtId),
        [rowId]: [thoughtId],
      };
    });
  };

  return (
    <div>
      {showIntro && (
        <section
          className='mx-auto my-8 w-full max-w-[1320px] min-w-0 px-4'
          style={{ boxSizing: 'border-box' }}
        >
          <div className='flex w-full min-w-0 flex-col items-start justify-center gap-3 rounded-lg border border-slate-300 bg-white p-4 text-left text-lg font-semibold shadow-md md:text-xl'>
            <p>
              Review the stressful child behavior, upsetting thought, and common
              parent response. Then match the corresponding reframed thought.
            </p>
            <p className='text-base font-normal md:text-lg'>
              Based on the reframed thinking you’ll see a new parent response.
            </p>
          </div>
        </section>
      )}

      <DragDropContext onDragStart={handleDragStart} onDragEnd={onDragEnd}>
        <div className={`error-pop ${showErrorMessage ? 'open' : 'closed'}`}>
          <p>Try Again!</p>
        </div>

        <div className='reframing-activity-layout'>
          <div className='reframing-bank-wrap'>
            <h2>Reframed Thoughts</h2>
            <StrictModeDroppable droppableId='bank' type='REFRAMING'>
              {(provided, snapshot) => (
                <div
                  className='reframing-bank'
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    backgroundColor: snapshot.isDraggingOver
                      ? '#f1f5f9'
                      : 'white',
                  }}
                >
                  {columns.bank.map((thoughtId, index) => (
                    <Draggable
                      key={thoughtId}
                      draggableId={thoughtId}
                      index={index}
                    >
                      {(dragProvided, snapshot) => (
                        <div
                          className='reframing-card reframing-card-source'
                          ref={(node) => {
                            dragProvided.innerRef(node);
                            cardRefs.current[thoughtId] = node;
                          }}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          style={{
                            ...dragProvided.draggableProps.style,
                            width:
                              snapshot.isDragging &&
                              draggingCardId === thoughtId &&
                              draggingCardSize?.width
                                ? `${draggingCardSize.width}px`
                                : '280px',
                            height:
                              snapshot.isDragging &&
                              draggingCardId === thoughtId &&
                              draggingCardSize?.height
                                ? `${draggingCardSize.height}px`
                                : undefined,
                          }}
                        >
                          {thoughtCards[thoughtId].content}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </StrictModeDroppable>
          </div>

          <div className='reframing-table-wrap'>
            <div className='reframing-table reframing-table-header'>
              <div className='reframing-cell'>Stressful Child Behavior</div>
              <div className='reframing-cell'>Upsetting Thought</div>
              <div className='reframing-cell'>Common Parent Response</div>
              <div className='reframing-cell'>
                Reframed Thought (Ask yourself: Is the reframed thought useful
                &amp; positive?)
              </div>
              <div className='reframing-cell'>New Parent Response</div>
            </div>

            {rows.map((row) => (
              <div className='reframing-table' key={row.id}>
                <div
                  className='reframing-cell'
                  data-label='Stressful Child Behavior'
                >
                  {row.stressBehavior}
                </div>
                <div className='reframing-cell' data-label='Upsetting Thought'>
                  {row.upsettingThought}
                </div>
                <div
                  className='reframing-cell'
                  data-label='Common Parent Response'
                >
                  {row.commonParentResponse}
                </div>
                <StrictModeDroppable
                  droppableId={row.id}
                  type='REFRAMING'
                  isDropDisabled={columns[row.id].length > 0}
                >
                  {(provided, snapshot) => (
                    <div
                      className={`reframing-cell reframing-drop-cell reframing-drop-cell-desktop ${
                        columns[row.id].length ? 'filled' : 'empty'
                      }`}
                      data-label='Reframed Thought'
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        backgroundColor: snapshot.isDraggingOver
                          ? '#f1f5f9'
                          : undefined,
                      }}
                    >
                      {columns[row.id].map((thoughtId, index) => (
                        <Draggable
                          key={thoughtId}
                          draggableId={thoughtId}
                          index={index}
                          isDragDisabled
                        >
                          {(dragProvided, snapshot) => (
                            <div
                              className='reframing-card reframing-card-placed reframing-card-locked'
                              ref={(node) => {
                                dragProvided.innerRef(node);
                                cardRefs.current[thoughtId] = node;
                              }}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              style={{
                                ...dragProvided.draggableProps.style,
                                width:
                                  snapshot.isDragging &&
                                  draggingCardId === thoughtId &&
                                  draggingCardSize?.width
                                    ? `${draggingCardSize.width}px`
                                    : '100%',
                                height:
                                  snapshot.isDragging &&
                                  draggingCardId === thoughtId &&
                                  draggingCardSize?.height
                                    ? `${draggingCardSize.height}px`
                                    : undefined,
                              }}
                            >
                              {thoughtCards[thoughtId].content}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {!columns[row.id].length && !snapshot.isDraggingOver && (
                        <div className='reframing-drop-hint'>
                          Drop the matching reframed thought here
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
                <div
                  className='reframing-cell reframing-select-cell'
                >
                  <label
                    className='reframing-select-label'
                    htmlFor={`reframed-thought-${row.id}`}
                  >
                    Reframed Thought
                  </label>
                  <p className='reframing-select-help'>
                    Ask yourself: Is the reframed thought useful and positive?
                  </p>
                  <select
                    id={`reframed-thought-${row.id}`}
                    value={columns[row.id][0] || ''}
                    onChange={(event) =>
                      handleThoughtSelection(row.id, event.target.value)
                    }
                    disabled={columns[row.id].length > 0}
                    aria-label={`Select the reframed thought for ${row.stressBehavior}`}
                  >
                    <option value='' disabled>
                      Select a reframed thought
                    </option>
                    {(columns[row.id].length
                      ? columns[row.id]
                      : columns.bank
                    ).map((thoughtId) => (
                      <option key={thoughtId} value={thoughtId}>
                        {thoughtCards[thoughtId].content}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  className='reframing-cell'
                  data-label='New Parent Response'
                >
                  {columns[row.id].length ? (
                    <div className='reframing-response-revealed'>
                      {row.newParentResponse}
                    </div>
                  ) : (
                    <div className='reframing-response-hidden' />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DragDropContext>
      {isComplete && showCompletionNotice && <ActivityCompletionNotice />}
      <ActivityBadge showBadge={showBadge} content={badge} dispatch={dispatch} />
    </div>
  );
};

export default ReframingActivity;
